import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum SosProposalLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemQty = 'itemQty', // quantity of the item for order
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  sell = 'sell', // price for sale
  extended = 'extended', // extended cost per (sell * itemQty), not to be entered by user
}

export const sosProposalFileDataSchema = yup.object().shape({
  company: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zip: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    url: yup.string().nullable().optional(),
  }),
  sosProposalNumber: yup.string().nullable().optional(),
  projectNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  quoteValidTill: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  poNumber: yup.string().nullable().optional(),
  salesPerson: yup.string().nullable().optional(),
  proposalFor: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zip: yup.string().nullable().optional(),
  }),
  installAt: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zip: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        sell: yup.string().nullable().optional(),
        extended: yup.string().nullable().optional(),
      }),
    )
    .required(),
  instructions: yup.string().nullable().optional(),
  shippingFee: yup.string().nullable().optional(),
  deposit: yup.string().nullable().optional(),
  signer: yup.object().shape({
    name: yup.string().nullable().optional(),
    signature: yup.string().nullable().optional(),
    entity: yup.string().nullable().optional(),
  }),
})

export type SosProposalFileData = yup.InferType<typeof sosProposalFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<SosProposalFileData>

export const sosProposalFileMappings: RequiredFileData = {
  company: {
    companyName: 'aa6d7488-e91c-4add-bbc7-6e2b5460ab49',
    address: '6883e14d-982d-44f5-b6b6-93ea6c143aef',
    city: '33cd3f4b-54ed-4a2d-81cc-5cccdf29af60',
    state: 'e797016e-8dfc-4560-ba7e-d3605170c9e9',
    zip: 'c0cad36f-ce70-43fc-b52c-43597d6fa7ef',
    phoneNumber: 'c8b0301d-5348-40c0-8969-1ebbf43eda7e',
    url: '6a8b11db-21e4-45ea-8114-3ff8c680f778',
  },
  proposalFor: {
    companyName: '00ed0ff1-574f-4e5a-be81-e08ad0a520d2',
    address: '7fc45c78-ce27-4044-8fa3-e6e11bf8f65f',
    city: 'be2e73d1-a49b-49f0-99cd-d28bb8cc0080',
    state: '73c2d71e-02ad-4087-984a-8846a4443174',
    zip: '6b4c7f93-3f7a-4675-aaf6-0ce92949b7f8',
  },
  sosProposalNumber: '8454311c-f0e1-4343-864b-36bed892def9',
  projectNumber: '46c00937-8ea7-4a30-a390-5c56b4fd59bd',
  paymentTerms: '31fe53dd-c05b-4b11-bb5b-341e442353a1',
  poNumber: 'eea72ec3-88a5-4fc9-a019-4086c257987e',
  dateIssued: '717bb14f-5237-4876-8fbf-2be4c5978034',
  quoteValidTill: 'af3ff9e0-42cb-4546-a108-ac7693137b34',
  installAt: {
    companyName: 'd37bcec7-4b55-4e14-bb03-b9bae852cc4e',
    address: '25286c32-49a6-481f-8dc3-250067326a0a',
    city: 'b2a915ff-b194-4d07-946b-0a0910613a0b',
    state: 'a7b4cd79-91ba-4fc2-80ac-8c8839a30019',
    zip: '902f4c3e-67ac-4813-b3aa-ebefc07d3c20',
  },
  salesPerson: 'fd46a153-3b4f-4335-98da-e23700d25a31',
  items: [
    {
      itemNumber: null,
      itemName: '18f7d82f-d544-4015-9a1a-43a7f85a52c8',
      description: '1568567c-1772-47b6-a6f7-e584d9c05de6',
      itemQty: '22fffb9e-fe21-4a48-ac19-d1f91b7052fb',
      sell: '8fafdd4f-6b1d-407c-8f10-3280d417384a',
      extended: null,
    },
  ],
  instructions: '46f01b99-eaf2-4a3a-8b53-9af2272bdfdd',
  shippingFee: '4e7e4deb-083a-4346-b496-b5ee2c3929b0',
  deposit: '60de0142-5741-40ba-9a5d-aa74228afc07',
  signer: {
    name: 'e1bb580e-8885-43d6-b2b2-ad1b108eef6e',
    signature: 'c2b6d6c6-d75c-4132-ada7-cea8ac6ef766',
    entity: 'd5f6c5cd-0e73-455e-9e83-7a3822b48c62',
  },
}
